/**
 * Pure decision logic for `./main`'s camera-acquisition flow — split out so it's
 * unit-testable without `navigator.mediaDevices`/a DOM, same seam `./url-guard`'s
 * `sameOriginUrl` and `./scare/prefs`'s `resolveReducedMotion` already use. `./main`
 * does the actual `getUserMedia()` race (impure) and defers to `cameraBannerFor`/
 * `isCameraRetryKey` here for what to tell the player and which key un-sticks them.
 *
 * The repro this fixes: a webcam already held by another app (OBS's own face-tracker,
 * another browser tab) makes `getUserMedia()` hang indefinitely rather than reject —
 * `./main` races it against `CAMERA_TIMEOUT_MS` so the Start click always resolves into
 * a playable game, in MOUSE mode if need be, with a banner naming the likely cause and
 * a retry key rather than silently waiting forever.
 */

export type CameraOutcome = "granted" | "timeout" | "denied" | "unsupported";

/** How long `./main` waits for `getUserMedia()` before giving up and falling back to
 * mouse steering. */
export const CAMERA_TIMEOUT_MS = 3000;

/** The `KeyboardEvent.key` (case-insensitive) that re-attempts camera/eye control after
 * a timeout or denial. */
export const CAMERA_RETRY_KEY = "r";

/** The banner text for each camera outcome, or `null` for a clean grant (no banner at
 * all). `timeout` names the likely cause (per the operator's live repro — another app
 * holding the camera) rather than a generic failure message; `timeout` and `denied` both
 * mention the retry key, `unsupported` doesn't (retrying can't change a browser's own
 * feature support). */
export function cameraBannerFor(outcome: CameraOutcome): string | null {
  switch (outcome) {
    case "granted":
      return null;
    case "unsupported":
      return "Camera not supported by this browser — steering with the mouse instead.";
    case "timeout":
      return (
        `Camera timed out after ${CAMERA_TIMEOUT_MS / 1000}s — likely another app is using it ` +
        `(OBS, another tab). Steering with the mouse instead. Press ` +
        `${CAMERA_RETRY_KEY.toUpperCase()} to retry eye control.`
      );
    case "denied":
      return (
        "Camera unavailable — steering with the mouse instead. Press " +
        `${CAMERA_RETRY_KEY.toUpperCase()} to retry eye control.`
      );
  }
}

/** True when `key` (a raw `KeyboardEvent.key`) is the camera-retry hotkey, case-insensitive. */
export function isCameraRetryKey(key: string): boolean {
  return key.toLowerCase() === CAMERA_RETRY_KEY;
}
